/**
 * CREATE WORKSPACE MODAL
 *
 * Premium workspace creation with Cloud vs Local type selection.
 * Local workspace option only available when running in Tauri desktop app.
 */

import { useState } from "react";
import { useWorkspaceStore, type WorkspaceType } from "@/stores/workspaceStore";
import { isTauri } from "@/hooks/usePlatform";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Cloud, HardDrive, Check, RefreshCw, Github, Sparkles, Users, Wifi, WifiOff, Shield, Lock, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { WORKSPACE_COLORS } from "@/lib/workspace-constants";

interface CreateWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateWorkspaceModal({ isOpen, onClose }: CreateWorkspaceModalProps) {
  const addWorkspace = useWorkspaceStore((s) => s.addWorkspace);
  const saveError = useWorkspaceStore((s) => s.saveError);
  const clearSaveError = useWorkspaceStore((s) => s.clearSaveError);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState(WORKSPACE_COLORS[0]);
  const [type, setType] = useState<WorkspaceType>("cloud");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canCreateLocal = isTauri();

  const handleCreate = async () => {
    if (!name.trim()) return;
    setError(null);
    setIsCreating(true);
    try {
      const result = await addWorkspace({ name: name.trim(), description, color, icon: "colony" }, type);
      if (!result) {
        // Read fresh error from store (closure value may be stale)
        const freshError = useWorkspaceStore.getState().saveError;
        setError(freshError || "Failed to create workspace");
        return;
      }
      onClose();
      setName("");
      setDescription("");
      setColor(WORKSPACE_COLORS[0]);
      setType("cloud");
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { clearSaveError(); onClose(); } }}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden gap-0">
        {/* Header with gradient accent */}
        <div className="px-6 pt-6 pb-4">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Create Workspace</DialogTitle>
            <DialogDescription>
              Choose a workspace type to get started.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 pb-6 space-y-5">
          {/* Type Cards */}
          <div className="grid grid-cols-2 gap-3">
            {/* Cloud Card */}
            <button
              type="button"
              onClick={() => setType("cloud")}
              className={cn(
                "relative flex flex-col rounded-xl border-2 p-4 text-left transition-all duration-200",
                "hover:scale-[1.02] active:scale-[0.98]",
                type === "cloud"
                  ? "border-[#00B2C8] bg-[#00B2C8]/5 shadow-[0_0_20px_-4px_rgba(0,178,200,0.3)]"
                  : "border-border hover:border-[#00B2C8]/40 bg-card"
              )}
            >
              {type === "cloud" && (
                <div className="absolute top-2.5 right-2.5">
                  <div className="w-5 h-5 rounded-full bg-[#00B2C8] flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                </div>
              )}
              <div className="w-9 h-9 rounded-lg bg-[#00B2C8]/10 flex items-center justify-center mb-3">
                <Cloud className="w-5 h-5 text-[#00B2C8]" />
              </div>
              <div className="font-semibold text-sm mb-1">Cloud</div>
              <div className="text-xs text-muted-foreground mb-3">Full-featured workspace</div>
              <div className="space-y-1.5 mt-auto">
                <Feature icon={RefreshCw} text="Synced across devices" />
                <Feature icon={Github} text="GitHub integration" />
                <Feature icon={Sparkles} text="AI pipeline & workflows" />
                <Feature icon={Users} text="Team collaboration" />
              </div>
            </button>

            {/* Local Card */}
            <button
              type="button"
              onClick={() => canCreateLocal && setType("local")}
              disabled={!canCreateLocal}
              className={cn(
                "relative flex flex-col rounded-xl border-2 p-4 text-left transition-all duration-200",
                canCreateLocal && "hover:scale-[1.02] active:scale-[0.98]",
                type === "local"
                  ? "border-amber-500 bg-amber-500/5 shadow-[0_0_20px_-4px_rgba(245,158,11,0.3)]"
                  : "border-border hover:border-amber-500/40 bg-card",
                !canCreateLocal && "opacity-50 cursor-not-allowed"
              )}
            >
              {type === "local" && (
                <div className="absolute top-2.5 right-2.5">
                  <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                </div>
              )}
              <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center mb-3">
                <HardDrive className="w-5 h-5 text-amber-500" />
              </div>
              <div className="font-semibold text-sm mb-1">Local</div>
              <div className="text-xs text-muted-foreground mb-3">
                {canCreateLocal ? "Offline-first workspace" : "Desktop app only"}
              </div>
              <div className="space-y-1.5 mt-auto">
                <Feature icon={WifiOff} text="Works offline" />
                <Feature icon={Shield} text="Data stays on device" />
                <Feature icon={Lock} text="No account required" />
                <Feature icon={Github} text="GitHub sync coming soon" dimmed />
              </div>
            </button>
          </div>

          {/* Name Input */}
          <div className="space-y-2">
            <Label htmlFor="ws-name" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Name
            </Label>
            <Input
              id="ws-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Workspace"
              className="h-9"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && name.trim() && handleCreate()}
            />
          </div>

          {/* Description Input */}
          <div className="space-y-2">
            <Label htmlFor="ws-desc" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Description
            </Label>
            <Input
              id="ws-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this workspace for?"
              className="h-9"
            />
          </div>

          {/* Color Picker */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Color
            </Label>
            <div className="flex gap-2 flex-wrap">
              {WORKSPACE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={cn(
                    "w-7 h-7 rounded-full transition-all duration-150 ring-offset-background",
                    color === c
                      ? "scale-110 ring-2 ring-offset-2 ring-white/70"
                      : "hover:scale-110 opacity-70 hover:opacity-100"
                  )}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Error */}
        {(error || saveError) && (
          <div className="px-6 pb-2">
            <p className="text-sm text-destructive">{error || saveError}</p>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-muted/30 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={isCreating} className="h-9">
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!name.trim() || isCreating}
            className="h-9 min-w-[100px] border-transparent"
            style={{
              backgroundColor: type === "cloud" ? "#00B2C8" : "#f59e0b",
            }}
          >
            {isCreating ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Workspace"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Feature({ icon: Icon, text, dimmed }: { icon: React.ComponentType<{ className?: string }>; text: string; dimmed?: boolean }) {
  return (
    <div className={cn("flex items-center gap-1.5 text-xs", dimmed ? "text-muted-foreground/50" : "text-muted-foreground")}>
      <Icon className="w-3 h-3 flex-shrink-0" />
      <span>{text}</span>
    </div>
  );
}
